import { useState } from "react";
import { Navigate, useNavigate, useSearchParams } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { api, errMsg } from "@/lib/api";
import { Btn, DoorMark, ErrorText, Field, Input } from "@/components/kit";

// Hidden, unlinked sign-in for test accounts and invitation links (no sign-up, no Google).
export default function SignIn() {
  const { me, refresh } = useAuth();
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const next = params.get("next") || "/projects";
  const mode = "signin";
  const [form, setForm] = useState({ name: "", email: "", password: "" });
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);

  if (me) return <Navigate to={next} replace />;

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    if (mode === "signup" && form.password.length < 8) return setError("Password must be at least 8 characters");
    setBusy(true);
    try {
      await api.post(mode === "signup" ? "/auth/signup" : "/auth/login", form);
      await refresh();
      navigate(next, { replace: true });
    } catch (err) {
      setError(errMsg(err));
    } finally {
      setBusy(false);
    }
  };

  const invite = next.startsWith("/invite/") || next.startsWith("/join/");

  return (
    <div className="grid min-h-screen grid-cols-[1.05fr_1fr] bg-[#F6F7F5]">
      <aside className="flex flex-col justify-between border-r border-[#DDE2DF] bg-[#EDF0EC] px-14 py-12">
        <div className="flex items-center gap-2 text-[#1F2526]">
          <DoorMark className="h-6 w-6 text-[#3B4446]" />
          <span className="font-display text-xl font-bold">Condoor</span>
        </div>
        <div className="max-w-md space-y-5 fade-in">
          <h1 className="font-display text-4xl font-bold leading-tight text-[#1F2526] lg:text-5xl">
            Door-hardware submittals, prepared and reviewed in one place.
          </h1>
          <p className="text-[15px] leading-relaxed text-[#4A5456]">
            Subcontractors, general contractors, and architects each keep their own documents and review context.
            Every source stays traceable to the page, row, or line it came from.
          </p>
          <ul className="space-y-1.5 text-[13.5px] text-[#3B4446]">
            <li>— Protected uploads with original source locations</li>
            <li>— Separate review context per organization</li>
            <li>— Copy-link invitations for your GC and architect</li>
          </ul>
        </div>
        <div className="text-xs text-[#6B7577]">Section 08 71 00 · Door hardware</div>
      </aside>

      <div className="flex items-center justify-center px-10 py-12">
        <div className="w-full max-w-[400px] space-y-6 fade-in">
          {invite && (
            <div className="rounded-md border border-[#B9D8D4] bg-[#E3F0EE] px-3 py-2 text-[13px] text-[#0F5E59]" data-testid="signin-invite-notice">
              Sign in to continue to your invitation.
            </div>
          )}
          <div>
            <h2 className="font-display text-2xl font-bold text-[#1F2526]" data-testid="signin-heading">Sign in</h2>
            <p className="mt-1 text-[13.5px] text-[#5B6567]">Test accounts and invitations only.</p>
          </div>

          <form className="space-y-3.5" onSubmit={submit} data-testid="auth-form">
            {mode === "signup" && (
              <Field label="Full name">
                <Input value={form.name} onChange={set("name")} required data-testid="signup-name-input" autoComplete="name" />
              </Field>
            )}
            <Field label="Email">
              <Input type="email" value={form.email} onChange={set("email")} required data-testid="auth-email-input" autoComplete="email" />
            </Field>
            <Field label="Password" hint={mode === "signup" ? "At least 8 characters" : null}>
              <Input type="password" value={form.password} onChange={set("password")} required data-testid="auth-password-input"
                autoComplete={mode === "signup" ? "new-password" : "current-password"} />
            </Field>
            <ErrorText data-testid="auth-error">{error}</ErrorText>
            <Btn variant="primary" className="w-full" type="submit" loading={busy} data-testid="auth-submit-button">
              {mode === "signup" ? "Create account" : "Sign in"}
            </Btn>
          </form>
        </div>
      </div>
    </div>
  );
}
